/**
 * Report section notices — which sections of a report carry a quarantined,
 * flagged, withheld or (admin) meta-leak note, derived purely from the report.
 *
 * 2026-10-13 (William: "i dont want any banners on report pages"). The notes
 * that used to render INSIDE report sections (SectionQuarantineBanner,
 * WithheldNarrativeNotice, MetaLeakChip) no longer render on the report page
 * — they stay in the delivered PDF / print tree only (`inSectionNotices`).
 * This module lists the same sections, using the same detections, so the
 * header status chip can name the state and the on-demand "Section quality
 * notes" dialog can offer what the notes offered: the reason, the original AI
 * output, and the Author / Re-run section actions.
 *
 * Pure: no React, no store.
 */
import type { SiteRagReport } from '../../../types/siteRag';
import type { ProviderStatus } from '../../../services/dataProviders';
import { detectInstructionShape } from '../../../lib/instructionShapeGuard';
import { isWithheldNarrativeNotice } from '../../../lib/reportRendering/scrubInternalSentinels';
import {
  formatReportLeafPath,
  withheldNarrativeLeaves,
  type ReportLeafPathSegment,
} from '../../../lib/report/withheldNarrative';

export type ReportSectionNoticeKind =
  /** The reviewer-voice gate withheld the narrative ("not assessed"). */
  | 'withheld'
  /** The gen-time guard quarantined instruction-shape AI output (SNT-1). */
  | 'quarantined'
  /** The render-time instruction-shape check flagged the text (SNT-3). */
  | 'flagged'
  /** Admin only — a meta-instruction leak was stripped (MEL-MR-2). */
  | 'meta-leak';

export interface ReportSectionNotice {
  /** Field path, e.g. `executiveSummary` or `waterAnalysis.waterRightsAnalysis`. */
  sectionId: string;
  label: string;
  kind: ReportSectionNoticeKind;
  /** Quarantined AI output — formerly behind "Show original AI output". */
  quarantinedOriginal?: string;
  /** The old in-section note offered "Author" (enter edit mode) here. */
  authorable: boolean;
  /** The old in-section note offered a section re-run here. */
  rerunnable: boolean;
}

/**
 * Every SectionQuarantineBanner mount on the report page, with the text it
 * checked at render time and the actions it was wired with (SiteRagReportView
 * passed `onAuthor` to the first three listed as authorable;
 * ProjectIntentOverviewSection passed `onRerun`).
 */
const QUARANTINE_MOUNTS: ReadonlyArray<{
  sectionId: string;
  label: string;
  text: (report: SiteRagReport) => string | null | undefined;
  authorable?: boolean;
  rerunnable?: boolean;
}> = [
  { sectionId: 'executiveSummary', label: 'Executive Summary', text: r => r.executiveSummary, authorable: true },
  { sectionId: 'projectIntentOverview', label: 'Project Intent & Site Overview', text: r => r.projectIntentOverview, rerunnable: true },
  { sectionId: 'sitingNotes', label: 'Siting Notes', text: r => r.sitingNotes, authorable: true },
  { sectionId: 'climateSevereWeather.implications', label: 'Climate Implications', text: r => r.climateSevereWeather?.implications, authorable: true },
  { sectionId: 'powerAnalysis.substationAssessment', label: 'Substation Assessment', text: r => r.powerAnalysis?.substationAssessment },
  { sectionId: 'waterAnalysis.supplyAvailability', label: 'Supply Availability', text: r => r.waterAnalysis?.supplyAvailability },
  { sectionId: 'testFitAnalysis.siteConstraints', label: 'Site Constraints', text: r => r.testFitAnalysis?.siteConstraints },
  { sectionId: 'interconnectionAnalysis.fiberAvailability', label: 'Fiber Availability', text: r => r.interconnectionAnalysis?.fiberAvailability },
  { sectionId: 'nepaScreening.rationale', label: 'NEPA Rationale', text: r => r.nepaScreening?.rationale },
];

const MOUNT_BY_ID = new Map(QUARANTINE_MOUNTS.map(mount => [mount.sectionId, mount]));

/** `waterAnalysis.waterRightsAnalysis` → "Water rights analysis". */
export function humanizeSectionPath(path: string): string {
  const known = MOUNT_BY_ID.get(path);
  if (known) return known.label;
  const last = path.split('.').reverse().find(segment => /[A-Za-z]/.test(segment.replace(/\[\d+\]/g, ''))) ?? path;
  const words = last.replace(/\[\d+\]/g, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase().trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : path;
}

/** Keys that never hold report narrative — skipped by the notice scan. */
const NON_NARRATIVE_KEYS = new Set([
  'datasets', 'timeSeries', 'references', 'sedText', 'providerStatuses', 'qualityWarnings',
  'quarantinedOriginal', 'editHistory', 'coverageCells', 'sectionGuardStatus', 'reportRevision',
]);
const SCAN_MAX_DEPTH = 8;

/**
 * Paths whose rendered text IS the withheld-narrative notice — legacy
 * placeholder bodies and leaves `materializeWithheldNarrative` filled in.
 */
function withheldNoticePaths(report: SiteRagReport): string[] {
  const found: string[] = [];
  const seen = new Set<object>();
  const walk = (value: unknown, path: ReportLeafPathSegment[]): void => {
    if (typeof value === 'string') {
      if (path.length > 0 && isWithheldNarrativeNotice(value)) found.push(formatReportLeafPath(path));
      return;
    }
    if (value === null || typeof value !== 'object' || path.length >= SCAN_MAX_DEPTH || seen.has(value)) return;
    seen.add(value);
    if (Array.isArray(value)) {
      value.forEach((entry, index) => walk(entry, [...path, index]));
      return;
    }
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      if (path.length === 0 && NON_NARRATIVE_KEYS.has(key)) continue;
      walk(entry, [...path, key]);
    }
  };
  walk(report, []);
  return found;
}

function metaLeakSectionIds(report: SiteRagReport): string[] {
  const ids: string[] = [];
  for (const warning of Array.isArray(report.qualityWarnings) ? report.qualityWarnings : []) {
    if (warning?.type !== 'meta-instruction-leak') continue;
    const samples = (warning as { samples?: ReadonlyArray<{ sectionId?: unknown }> }).samples;
    for (const sample of Array.isArray(samples) ? samples : []) {
      if (typeof sample?.sectionId === 'string' && sample.sectionId && !ids.includes(sample.sectionId)) {
        ids.push(sample.sectionId);
      }
    }
  }
  return ids;
}

/**
 * Every section note the report page used to render in-section, in a stable
 * order: withheld narratives, then quarantined / flagged sections (mount
 * order), then — for admins — meta-instruction leaks.
 */
export function collectReportSectionNotices(
  report: SiteRagReport,
  { isAdmin = false }: { isAdmin?: boolean } = {},
): ReportSectionNotice[] {
  const notices: ReportSectionNotice[] = [];
  const has = (sectionId: string, kind: ReportSectionNoticeKind) =>
    notices.some(notice => notice.sectionId === sectionId && notice.kind === kind);

  // 1. Withheld narratives — the persisted record first (its labels are the
  //    customer-facing ones), then any rendered notice text it does not cover.
  for (const leaf of withheldNarrativeLeaves(report)) {
    notices.push({ sectionId: leaf.path, label: leaf.label, kind: 'withheld', authorable: false, rerunnable: false });
  }
  for (const path of withheldNoticePaths(report)) {
    if (has(path, 'withheld')) continue;
    notices.push({ sectionId: path, label: humanizeSectionPath(path), kind: 'withheld', authorable: false, rerunnable: false });
  }
  const withheldIds = new Set(notices.map(notice => notice.sectionId));

  // 2. Quarantine — the gen-time guard status, then the render-time check the
  //    in-section banner ran on the text it was given.
  const guard = report.sectionGuardStatus ?? {};
  const guardIds = [
    ...QUARANTINE_MOUNTS.map(mount => mount.sectionId).filter(id => guard[id] === 'degraded-instruction-leak'),
    ...Object.keys(guard).filter(id => guard[id] === 'degraded-instruction-leak' && !MOUNT_BY_ID.has(id)),
  ];
  const quarantined = new Set(guardIds);
  for (const mount of QUARANTINE_MOUNTS) {
    if (withheldIds.has(mount.sectionId)) continue;
    if (quarantined.has(mount.sectionId)) {
      const original = report.quarantinedOriginal?.[mount.sectionId];
      notices.push({
        sectionId: mount.sectionId,
        label: mount.label,
        kind: 'quarantined',
        ...(original ? { quarantinedOriginal: original } : {}),
        authorable: Boolean(mount.authorable),
        rerunnable: Boolean(mount.rerunnable),
      });
      continue;
    }
    const text = mount.text(report);
    if (typeof text === 'string' && !isWithheldNarrativeNotice(text) && detectInstructionShape(text).flagged) {
      notices.push({
        sectionId: mount.sectionId,
        label: mount.label,
        kind: 'flagged',
        authorable: Boolean(mount.authorable),
        rerunnable: Boolean(mount.rerunnable),
      });
    }
  }
  for (const id of guardIds) {
    if (MOUNT_BY_ID.has(id) || withheldIds.has(id)) continue;
    const original = report.quarantinedOriginal?.[id];
    notices.push({
      sectionId: id,
      label: humanizeSectionPath(id),
      kind: 'quarantined',
      ...(original ? { quarantinedOriginal: original } : {}),
      authorable: false,
      rerunnable: false,
    });
  }

  // 3. Meta-instruction leaks — the in-section chip was admin-only, and so
  //    was its section re-run.
  if (isAdmin) {
    for (const id of metaLeakSectionIds(report)) {
      notices.push({ sectionId: id, label: humanizeSectionPath(id), kind: 'meta-leak', authorable: false, rerunnable: true });
    }
  }
  return notices;
}

/** One sentence per notice — shared by the chip detail and the dialog. */
export function describeSectionNotice(notice: ReportSectionNotice): string {
  switch (notice.kind) {
    case 'withheld':
      return `${notice.label}: narrative withheld by an automated quality check; not assessed in this report (treat as open, not cleared).`;
    case 'quarantined':
      return `${notice.label}: the AI output read as instruction-shape text rather than grounded findings, so a data-grounded fallback is shown.`;
    case 'flagged':
      return `${notice.label}: flagged at render time as possibly instruction-shape text; consider re-running.`;
    case 'meta-leak':
      return `${notice.label}: a meta-instruction leak was stripped from the prose; the section may need a re-run to repopulate cleanly.`;
  }
}

/** Providers whose failure is a configuration problem (PU4) — a rerun won't help. */
export function misconfiguredProviders(statuses: readonly ProviderStatus[] | null | undefined): ProviderStatus[] {
  return (statuses ?? []).filter(status => status?.failureMode === 'misconfigured');
}
